import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, gql } from '@apollo/client'
import Card from '@/components/common/Card'

import {
    GET_CAMPAIGN_SUBSCRIPTIONS,
    GET_PLANS,
    CREATE_CAMPAIGN_SUBSCRIPTION,
    EXTEND_CAMPAIGN_SUBSCRIPTION,
    GET_CAMPAIGN_CREATIVES_QUOTA,
    UPDATE_CAMPAIGN_SUBSCRIPTION_AUTO_RENEW,
    RETRY_CAMPAIGN_SUBSCRIPTION_PAYMENT,
    RECOVER_PAID_CAMPAIGN_ACTIVATION,
} from '@/graphql/subscription-queries'
import { GET_CAMPAIGNS_FOR_PICKER } from '@/graphql/campaign-queries'
import { USER_FIELDS } from '@/graphql/user-fragments'
import { AuthContext } from '@/context/auth-context'
import { SUBSCRIPTION_PLANS } from './config'
import { SUBSCRIPTION_CREATIVE, SUBSCRIPTION_LICENSE, SUBSCRIPTION_SUPPORT, isSubscriptionEndedStatus } from './constants'
import { typeToConfigKey } from './planResolver'
import { SubscriptionsIcon } from '@/components/icons/new'
import { CubeLoader } from '@/components/ui/cube-loader'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { InfoIcon } from '@/components/icons'
import AddCardInline from '@/components/payments/AddCardInline'
import Notification from '@/components/partials/Notification'
import { showNotification } from '@/utils/notifications'
import { useSubscriptionPlanSelection } from './useSubscriptionPlanSelection'
import CurrentPlanCard from './CurrentPlanCard/CurrentPlanCard'
import { useActionLock } from '@/hooks/useActionLock'

const ACTIVE_STATUSES = new Set(['active', 'trialing'])

// Subscriptions that exist in Stripe and still have an invoice that can be settled. Distinct from
// both live and finished: the customer owes money and can pay it without subscribing again.
const UNSETTLED_STATUSES = new Set(['incomplete', 'past_due', 'unpaid'])

/**
 * Which licence the screen should show, derived from the `?license=` id and the loaded list —
 * never stored separately, so a change to either is picked up on the next render.
 *
 * Nothing is decided until the list has arrived: the customer can arrive from a link, the list is
 * briefly empty, the requested licence looks absent, and (were a decision made here) the screen
 * would settle on somebody else's — on the page where subscriptions are paid for. While loading,
 * a requested id is assumed valid so its subscriptions can start fetching without waiting.
 *
 * A requested id that really is not in the list is reported as `unavailable` rather than silently
 * replaced by the first licence — the customer asked for a specific one.
 */
export const resolveSelectedLicense = ({ requested, licenses, loaded }) => {
    if (!requested) {
        return { status: loaded ? 'resolved' : 'pending', id: loaded ? (licenses[0]?.id ?? '') : '' }
    }
    if (!loaded) return { status: 'pending', id: requested }
    if (licenses.some((license) => license.id === requested)) return { status: 'resolved', id: requested }
    return { status: 'unavailable', id: requested }
}

const planLabel = (uiPlanId) => SUBSCRIPTION_PLANS.find((p) => p.id === uiPlanId)?.title ?? 'Plan'

const GET_CURRENT_USER = gql`
    ${USER_FIELDS}
    query SubscriptionsGetUser($where: UserWhere) {
        users(where: $where) {
            ...UserFields
        }
    }
`

const Subscriptions = () => {
    const { user: authUser } = useContext(AuthContext)

    const [searchParams, setSearchParams] = useSearchParams()
    const requestedLicenseId = searchParams.get('license') ?? ''
    const [pendingPlanId, setPendingPlanId] = useState(null)
    const [extendingPlanId, setExtendingPlanId] = useState(null)
    const [extendFrequencies, setExtendFrequencies] = useState({})
    const [extendTiers, setExtendTiers] = useState({})
    const [pendingAutoRenewPlanId, setPendingAutoRenewPlanId] = useState(null)
    const [retryingPlanId, setRetryingPlanId] = useState(null)

    const { data: campaignsData, loading: licensesLoading, refetch: refetchCampaigns } = useQuery(GET_CAMPAIGNS_FOR_PICKER, {
        fetchPolicy: 'cache-and-network',
    })
    const campaigns = useMemo(() => campaignsData?.campaigns ?? [], [campaignsData])
    const licenseCampaigns = useMemo(() => campaigns.filter((c) => c.hasSubscriptions === true), [campaigns])

    const licensesLoaded = Boolean(campaignsData)

    const selection = useMemo(
        () => resolveSelectedLicense({ requested: requestedLicenseId, licenses: licenseCampaigns, loaded: licensesLoaded }),
        [requestedLicenseId, licenseCampaigns, licensesLoaded],
    )
    // Subscriptions are fetched for a `pending`/`resolved` id (assumed valid until proven otherwise,
    // so a deep link does not wait on the picker query) but never for one the list has rejected.
    const license = selection.status === 'unavailable' ? '' : selection.id
    const selectedCampaign = licenseCampaigns.find((campaign) => campaign.id === license)

    const handleLicenseChange = (id) => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                if (id) next.set('license', id)
                else next.delete('license')
                return next
            },
            { replace: false },
        )
    }

    const { data: userData, refetch: refetchUser } = useQuery(GET_CURRENT_USER, {
        variables: { where: { id: authUser?.id } },
        skip: !authUser?.id,
        fetchPolicy: 'cache-and-network',
    })
    const paymentMethodId = userData?.users?.[0]?.paymentMethodId ?? null

    const { data: plansData } = useQuery(GET_PLANS)
    const plans = useMemo(() => plansData?.plans ?? [], [plansData])

    const {
        billingCycles,
        setBillingCycles,
        creativeTier,
        setCreativeTier,
        creativeFrequency,
        setCreativeFrequency,
        handleCycleChange,
        handleTierChange,
        handleCreativeFrequencyChange,
        selectedPlans,
        resolvePlanIdForUi,
    } = useSubscriptionPlanSelection({ plans })

    const {
        data: subsData,
        loading: subsLoading,
        refetch: refetchSubscriptions,
    } = useQuery(GET_CAMPAIGN_SUBSCRIPTIONS, {
        variables: { campaignId: license },
        skip: !license,
        fetchPolicy: 'cache-and-network',
    })
    const subscriptions = useMemo(() => subsData?.getCampaignSubscriptions ?? [], [subsData])

    const { activeByConfigKey, endedByConfigKey, unsettledByConfigKey } = useMemo(() => {
        const active = {}
        const latest = {}
        subscriptions.forEach((sub) => {
            const key = typeToConfigKey[sub.type]
            if (!key) return
            if (!latest[key]) latest[key] = sub
            if (ACTIVE_STATUSES.has(sub.status) && !active[key]) active[key] = sub
        })
        const ended = {}
        Object.entries(latest).forEach(([key, sub]) => {
            if (!active[key] && isSubscriptionEndedStatus(sub.status)) ended[key] = sub
        })
        const unsettled = {}
        subscriptions.forEach((sub) => {
            const key = typeToConfigKey[sub.type]
            if (!key) return
            if (!active[key] && !unsettled[key] && UNSETTLED_STATUSES.has(sub.status)) unsettled[key] = sub
        })
        return { activeByConfigKey: active, endedByConfigKey: ended, unsettledByConfigKey: unsettled }
    }, [subscriptions])

    const endedMatchedPlanByConfigKey = useMemo(() => {
        const out = {}
        Object.entries(endedByConfigKey).forEach(([key, sub]) => {
            if (sub) out[key] = plans.find((p) => p.id === sub.planId) ?? null
        })
        return out
    }, [endedByConfigKey, plans])

    // Seed cycle/tier dropdowns from the most recent ended subscription, once per
    // license. Lets the "Re-new" action default to the same plan the user previously
    // had. Subsequent manual selections persist until the user switches license.
    const seededLicenseRef = useRef(null)
    useEffect(() => {
        if (!license || subsLoading) return
        if (seededLicenseRef.current === license) return
        seededLicenseRef.current = license

        const endedLicense = endedByConfigKey[SUBSCRIPTION_LICENSE]
        const endedSupport = endedByConfigKey[SUBSCRIPTION_SUPPORT]
        const endedCreative = endedByConfigKey[SUBSCRIPTION_CREATIVE]

        if (endedLicense?.frequency || endedSupport?.frequency) {
            setBillingCycles((prev) => ({
                ...prev,
                ...(endedLicense?.frequency ? { [SUBSCRIPTION_LICENSE]: endedLicense.frequency } : {}),
                ...(endedSupport?.frequency ? { [SUBSCRIPTION_SUPPORT]: endedSupport.frequency } : {}),
            }))
        }
        if (endedCreative?.frequency) setCreativeFrequency(endedCreative.frequency)
        const endedCreativeTier = endedMatchedPlanByConfigKey[SUBSCRIPTION_CREATIVE]?.tier
        if (endedCreativeTier) setCreativeTier(endedCreativeTier)
    }, [
        license,
        subsLoading,
        endedByConfigKey,
        endedMatchedPlanByConfigKey,
        setBillingCycles,
        setCreativeFrequency,
        setCreativeTier,
    ])

    const [createCampaignSubscription] = useMutation(CREATE_CAMPAIGN_SUBSCRIPTION, {
        onCompleted: async (_data, clientOptions) => {
            const uiPlanId = clientOptions?.context?.uiPlanId ?? null
            await refetchSubscriptions()
            if (uiPlanId === SUBSCRIPTION_CREATIVE) {
                await refetchCreativesQuota()
            }
            const label = uiPlanId ? planLabel(uiPlanId) : 'Subscription'
            showNotification(`${label} subscription activated.`, 'success', 3000)
        },
        onError: (error, clientOptions) => {
            const uiPlanId = clientOptions?.context?.uiPlanId ?? null
            const message = error.graphQLErrors?.[0]?.message || error.message
            const label = uiPlanId ? planLabel(uiPlanId) : 'Subscription'
            showNotification(message || `Could not add ${label}. Please try again.`, 'error', 5000)
        },
    })

    // Keyed by licence then by plan id, so switching licences never shows a stale link for a
    // different licence's subscription in the same slot.
    const [bankActionByLicense, setBankActionByLicense] = useState({})
    const bankActionForPlan = (uiPlanId) => bankActionByLicense[license]?.[uiPlanId] ?? null

    const setBankAction = (uiPlanId, actionUrl) => {
        setBankActionByLicense((prev) => ({
            ...prev,
            [license]: { ...(prev[license] ?? {}), [uiPlanId]: actionUrl },
        }))
    }

    const clearBankAction = (uiPlanId) => {
        setBankActionByLicense((prev) => {
            if (!(uiPlanId in (prev[license] ?? {}))) return prev
            const rest = { ...prev[license] }
            delete rest[uiPlanId]
            return { ...prev, [license]: rest }
        })
    }

    const [retryCampaignSubscriptionPayment] = useMutation(RETRY_CAMPAIGN_SUBSCRIPTION_PAYMENT, {
        onCompleted: async (data, clientOptions) => {
            const uiPlanId = clientOptions?.context?.uiPlanId ?? null
            await refetchSubscriptions()
            const result = data?.retryCampaignSubscriptionPayment
            if (result?.outcome === 'ACTION_REQUIRED') {
                // The bank wants the customer to confirm. `window.open` with `noopener` always returns
                // null, so its return value can't tell us whether the tab actually opened — a popup
                // blocker looks identical to success. The message stays neutral and a visible link
                // stays on screen either way, until cleared, so there is always a way to finish.
                if (!result.actionUrl) {
                    showNotification('Your bank needs you to confirm this payment. Please try again shortly.', 'info', 8000)
                    return
                }
                if (uiPlanId) setBankAction(uiPlanId, result.actionUrl)
                window.open(result.actionUrl, '_blank', 'noopener,noreferrer')
                showNotification(
                    'Your bank needs you to confirm this payment. If a new tab didn\'t open, use the "Confirm payment with your bank" link.',
                    'info',
                    8000,
                )
                return
            }
            if (uiPlanId) clearBankAction(uiPlanId)
            if (result?.outcome === 'ALREADY_PAID') {
                showNotification('This subscription was already paid. Nothing further was charged.', 'success', 5000)
                return
            }
            showNotification('Payment complete. Thank you!', 'success', 4000)
        },
        onError: (error) => {
            showNotification(error.graphQLErrors?.[0]?.message || error.message, 'error', 6000)
        },
    })

    const [recoverPaidCampaignActivation, { loading: recoveringActivation }] = useMutation(
        RECOVER_PAID_CAMPAIGN_ACTIVATION,
        {
            onCompleted: async () => {
                await refetchCampaigns()
                showNotification('Campaign activated. No additional payment was taken.', 'success', 5000)
            },
            onError: (error) => {
                showNotification(error.graphQLErrors?.[0]?.message || error.message, 'error', 6000)
            },
        },
    )

    const [extendCampaignSubscription] = useMutation(EXTEND_CAMPAIGN_SUBSCRIPTION, {
        onCompleted: async (_data, clientOptions) => {
            const uiPlanId = clientOptions?.context?.uiPlanId ?? null
            await refetchSubscriptions()
            const label = uiPlanId ? planLabel(uiPlanId) : 'Subscription'
            showNotification(`${label} subscription extended.`, 'success', 3000)
        },
        onError: (error, clientOptions) => {
            const uiPlanId = clientOptions?.context?.uiPlanId ?? null
            const message = error.graphQLErrors?.[0]?.message || error.message
            const label = uiPlanId ? planLabel(uiPlanId) : 'Subscription'
            showNotification(message || `Could not extend ${label}. Please try again.`, 'error', 5000)
        },
    })

    const [updateCampaignSubscriptionAutoRenew] = useMutation(UPDATE_CAMPAIGN_SUBSCRIPTION_AUTO_RENEW, {
        onCompleted: async () => {
            await refetchSubscriptions()
            showNotification('Billing preferences updated.', 'success', 3000)
        },
        onError: (error) => {
            const message = error.graphQLErrors?.[0]?.message || error.message
            showNotification(message || 'Could not update auto-renew settings.', 'error', 5000)
        },
    })

    const handleAutoRenewChange = async (uiPlanId, cancelAtPeriodEnd) => {
        const sub = activeByConfigKey[uiPlanId]
        if (!sub) return
        setPendingAutoRenewPlanId(uiPlanId)
        try {
            await updateCampaignSubscriptionAutoRenew({
                variables: { input: { subscriptionId: sub.id, cancelAtPeriodEnd } },
            })
        } finally {
            setPendingAutoRenewPlanId(null)
        }
    }

    const retryLock = useActionLock()
    const handleRetryPayment = (uiPlanId) =>
        retryLock(async () => {
            const sub = unsettledByConfigKey[uiPlanId]
            if (!sub) return
            setRetryingPlanId(uiPlanId)
            try {
                await retryCampaignSubscriptionPayment({
                    context: { uiPlanId },
                    variables: { input: { subscriptionId: sub.id } },
                })
            } finally {
                setRetryingPlanId(null)
            }
        })

    const activationRecoveryLock = useActionLock()
    const handleRecoverPaidActivation = () =>
        activationRecoveryLock(async () => {
            if (!license) return
            await recoverPaidCampaignActivation({ variables: { campaignId: license } })
        })

    const activeLicense = activeByConfigKey[SUBSCRIPTION_LICENSE]
    const canRecoverActivation =
        selectedCampaign?.systemStatus === 'PENDING' && Boolean(activeLicense)

    const handleExtendFrequencyChange = (uiPlanId, frequency) => {
        setExtendFrequencies((prev) => ({ ...prev, [uiPlanId]: frequency }))
    }

    const handleExtendTierChange = (uiPlanId, tier) => {
        setExtendTiers((prev) => ({ ...prev, [uiPlanId]: tier }))
    }

    const { data: quotaData, refetch: refetchCreativesQuota } = useQuery(GET_CAMPAIGN_CREATIVES_QUOTA, {
        variables: { campaignId: license },
        skip: !license,
        fetchPolicy: 'cache-and-network',
    })
    const quota = quotaData?.getCampaignCreativesQuota ?? null

    const getExtendFrequency = (uiPlanId) => extendFrequencies[uiPlanId] || ''

    const extendLock = useActionLock()
    const handleExtend = (uiPlanId) =>
        extendLock(async () => {
            const sub = activeByConfigKey[uiPlanId]
            if (!sub) {
                showNotification('No active subscription to extend.', 'error', 4000)
                return
            }
            if (!paymentMethodId) {
                showNotification('Please add a payment method before extending.', 'error', 4000)
                return
            }
            const frequency = getExtendFrequency(uiPlanId)
            if (!frequency) {
                showNotification('Please select a frequency before upgrading.', 'error', 4000)
                return
            }
            const tier = uiPlanId === SUBSCRIPTION_CREATIVE ? extendTiers[uiPlanId] || null : null
            if (uiPlanId === SUBSCRIPTION_CREATIVE && !tier) {
                showNotification('Please select a tier before upgrading.', 'error', 4000)
                return
            }
            setExtendingPlanId(uiPlanId)
            try {
                await extendCampaignSubscription({
                    context: { uiPlanId },
                    variables: {
                        input: {
                            subscriptionId: sub.id,
                            frequency,
                            paymentMethodId,
                            ...(tier ? { tier } : {}),
                        },
                    },
                })
                if (uiPlanId === SUBSCRIPTION_CREATIVE) {
                    await refetchCreativesQuota()
                }
            } finally {
                setExtendingPlanId(null)
            }
        })

    const addLock = useActionLock()
    const handleAdd = (uiPlanId) =>
        addLock(async () => {
            if (!license) {
                showNotification('Please select a license first.', 'error', 4000)
                return
            }
            const planId = resolvePlanIdForUi(uiPlanId)
            if (!planId) {
                showNotification('No matching plan is configured. Please contact your administrator.', 'error', 5000)
                return
            }
            if (!paymentMethodId) {
                showNotification('Please add a payment method before subscribing.', 'error', 4000)
                return
            }
            setPendingPlanId(uiPlanId)
            try {
                await createCampaignSubscription({
                    context: { uiPlanId },
                    variables: { input: { campaignId: license, planId, paymentMethodId } },
                })
            } finally {
                setPendingPlanId(null)
            }
        })

    return (
        <>
            <Card className="page-card">
                <Card.Header className="tw:flex tw:flex-row tw:flex-nowrap tw:items-center tw:justify-between tw:gap-3 tw:min-w-0">
                    <div className="d-flex align-items-center tw:gap-3 tw:min-w-0 tw:shrink">
                        <i className="icon text-gray-200 me-1 tw:shrink-0">
                            <SubscriptionsIcon size={32} />
                        </i>
                        <h5 className="title mb-0 text-white tw:truncate">Subscriptions</h5>
                    </div>
                    <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2 tw:min-w-0">
                        <div className="tw:flex tw:items-center tw:gap-1.5 tw:shrink-0">
                            <span id="subscriptions-license-label" className="tw:text-sm tw:font-medium tw:text-white">
                                License
                            </span>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="tw:inline-flex tw:items-center tw:justify-center tw:rounded-sm tw:text-[#97AFCD] hover:tw:text-white"
                                        aria-label="License selection help"
                                    >
                                        <InfoIcon className="tw:size-6 tw:shrink-0" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent className="tw:max-w-xs">
                                    Select a license to view related subscriptions.
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        <Select
                            value={selection.status === 'unavailable' ? '' : selection.id}
                            onValueChange={handleLicenseChange}
                            disabled={licenseCampaigns.length === 0}
                        >
                            <SelectTrigger
                                className="tw:w-[260px] tw:max-w-full tw:shrink-0"
                                aria-labelledby="subscriptions-license-label"
                            >
                                <SelectValue placeholder="Select a license" />
                            </SelectTrigger>
                            <SelectContent>
                                {licenseCampaigns.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </Card.Header>
                <Card.Body className="tw:!pb-6">
                    {licensesLoading ? (
                        <CubeLoader size="default" fullScreen />
                    ) : licenseCampaigns.length === 0 ? (
                        <div className="tw:py-10 tw:text-center tw:text-sm tw:text-[#97AFCD]">
                            No licenses with subscription available. Only licenses that already have subscription
                            billing are listed here.
                        </div>
                    ) : selection.status === 'unavailable' ? (
                        <div className="tw:py-10 tw:text-center tw:text-sm tw:text-[#97AFCD]">
                            This licence isn't available. Pick another licence from the selector above.
                        </div>
                    ) : !license ? (
                        <div className="tw:py-10 tw:text-center tw:text-sm tw:text-[#97AFCD]">
                            Select a license to view subscriptions.
                        </div>
                    ) : subsLoading ? (
                        <CubeLoader size="default" fullScreen={true} />
                    ) : (
                        <>
                            {canRecoverActivation && (
                                <div className="tw:mb-4 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded tw:border tw:border-amber-500/40 tw:bg-amber-500/10 tw:p-4">
                                    <p className="tw:m-0 tw:text-sm tw:text-white">
                                        Your licence is paid, but this campaign is still pending activation. You can safely retry activation without another charge.
                                    </p>
                                    <Button type="button" onClick={handleRecoverPaidActivation} disabled={recoveringActivation}>
                                        {recoveringActivation ? 'Activating…' : 'Activate campaign'}
                                    </Button>
                                </div>
                            )}
                            {!paymentMethodId && (
                                <div className="tw:mb-4">
                                    <AddCardInline
                                        title="Add a payment method"
                                        description="Save a card to enable subscriptions for this license."
                                        onSaved={() => refetchUser()}
                                    />
                                </div>
                            )}
                            <div className="tw:flex tw:flex-col tw:gap-4">
                                {SUBSCRIPTION_PLANS.map((planDef) => {
                                    const activeSub = activeByConfigKey[planDef.id] ?? null
                                    const endedSub = endedByConfigKey[planDef.id] ?? null
                                    const selected = selectedPlans[planDef.id] ?? null
                                    const matchedPlan = activeSub
                                        ? (plans.find((p) => p.id === activeSub.planId) ?? null)
                                        : selected
                                    const endedMatchedPlan = endedMatchedPlanByConfigKey[planDef.id] ?? null
                                    return (
                                        <CurrentPlanCard
                                            key={planDef.id}
                                            planDef={planDef}
                                            subscription={activeSub}
                                            endedSubscription={endedSub}
                                            unsettledSubscription={unsettledByConfigKey[planDef.id] ?? null}
                                            onRetryPayment={handleRetryPayment}
                                            retrying={retryingPlanId === planDef.id}
                                            bankActionUrl={bankActionForPlan(planDef.id)}
                                            endedPlan={endedMatchedPlan}
                                            plan={matchedPlan}
                                            cycle={billingCycles[planDef.id]}
                                            tier={creativeTier}
                                            creativeFrequency={
                                                planDef.id === SUBSCRIPTION_CREATIVE ? creativeFrequency : undefined
                                            }
                                            onCycleChange={handleCycleChange}
                                            onTierChange={handleTierChange}
                                            onCreativeFrequencyChange={
                                                planDef.id === SUBSCRIPTION_CREATIVE
                                                    ? handleCreativeFrequencyChange
                                                    : undefined
                                            }
                                            onAdd={handleAdd}
                                            pending={pendingPlanId === planDef.id}
                                            extendFrequency={getExtendFrequency(planDef.id)}
                                            onExtendFrequencyChange={handleExtendFrequencyChange}
                                            extendTier={
                                                planDef.id === SUBSCRIPTION_CREATIVE
                                                    ? extendTiers[planDef.id] || ''
                                                    : undefined
                                            }
                                            onExtendTierChange={
                                                planDef.id === SUBSCRIPTION_CREATIVE
                                                    ? handleExtendTierChange
                                                    : undefined
                                            }
                                            onExtend={handleExtend}
                                            extending={extendingPlanId === planDef.id}
                                            quota={planDef.id === SUBSCRIPTION_CREATIVE ? quota : null}
                                            onAutoRenewChange={(cancelAtPeriodEnd) =>
                                                handleAutoRenewChange(planDef.id, cancelAtPeriodEnd)
                                            }
                                            autoRenewPending={pendingAutoRenewPlanId === planDef.id}
                                        />
                                    )
                                })}
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
            <Notification />
        </>
    )
}

export default Subscriptions
